import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import { memo, useCallback } from 'react';

import { tailwindVariants } from '@air/utils-tailwind';

import {
  isWorkspaceAgentWatermarkPlacementId,
  WORKSPACE_AGENT_WATERMARK_PLACEMENTS,
  type WorkspaceAgentWatermarkPlacementId,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentWatermark';

const placementMark = tailwindVariants({
  base: 'pointer-events-none absolute ads:bg-ads-gray-7 bg-gray-7 motion-safe:transition-[width,height,background-color] motion-safe:duration-200 motion-safe:ease-out',
  variants: {
    checked: {
      true: 'h-2.5 w-5 rounded-2 ads:bg-primary bg-blue-9',
      false: 'size-1.5 rounded-full ads:bg-ads-gray-7 bg-gray-7',
    },
    placement: {
      'bottom-center': 'bottom-0 left-1/2 -translate-x-1/2',
      'bottom-left': 'bottom-0 left-0',
      'bottom-right': 'right-0 bottom-0',
      center: 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
      'middle-left': 'top-1/2 left-0 -translate-y-1/2',
      'middle-right': 'top-1/2 right-0 -translate-y-1/2',
      'top-center': 'top-0 left-1/2 -translate-x-1/2',
      'top-left': 'top-0 left-0',
      'top-right': 'top-0 right-0',
    },
  },
});

export type WorkspaceAgentGuidedVariantWatermarkPlacementFieldProps = {
  onChange: (placement: WorkspaceAgentWatermarkPlacementId) => void;
  placement: WorkspaceAgentWatermarkPlacementId;
};

export const WorkspaceAgentGuidedVariantWatermarkPlacementField = memo(
  ({ onChange, placement }: WorkspaceAgentGuidedVariantWatermarkPlacementFieldProps) => {
    const handleValueChange = useCallback(
      (nextPlacement: string) => {
        if (isWorkspaceAgentWatermarkPlacementId(nextPlacement)) {
          onChange(nextPlacement);
        }
      },
      [onChange],
    );

    return (
      <fieldset className="row-span-2 m-0 border-0 p-0">
        <legend className="mb-1 font-medium ads:text-primary text-12 text-gray-12">Placement</legend>
        {/* Padding is the only inset: each mark anchors to its cell's placement edge, so the nine marks sit on the panel edges. */}
        <RadixRadioGroup.Root
          className="grid h-[88px] w-[100px] grid-cols-3 grid-rows-3 gap-0 rounded-2 ads:bg-subtle bg-gray-3 p-1"
          data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_PLACEMENT_GRID"
          onValueChange={handleValueChange}
          value={placement}
        >
          {WORKSPACE_AGENT_WATERMARK_PLACEMENTS.map((option) => (
            <RadixRadioGroup.Item
              aria-label={option.label}
              className="relative size-full cursor-pointer rounded-2 focus-visible:outline ads:focus-visible:outline-focus focus-visible:outline-2 focus-visible:outline-blue-9 focus-visible:outline-offset-1 [&:not([data-state=checked]):hover>span]:size-2 [&:not([data-state=checked]):hover>span]:bg-gray-9 ads:[&:not([data-state=checked]):hover>span]:bg-ads-gray-9"
              data-placement={option.id}
              data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_PLACEMENT"
              key={option.id}
              title={option.label}
              value={option.id}
            >
              <span
                aria-hidden
                className={placementMark({
                  checked: placement === option.id,
                  placement: option.id,
                })}
                data-testid="WORKSPACE_AGENT_GUIDED_VARIANT_WATERMARK_PLACEMENT_DOT"
              />
            </RadixRadioGroup.Item>
          ))}
        </RadixRadioGroup.Root>
      </fieldset>
    );
  },
);

WorkspaceAgentGuidedVariantWatermarkPlacementField.displayName = 'WorkspaceAgentGuidedVariantWatermarkPlacementField';
